import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { ClientApiProps, ICallsignReconfirmationRequestEx, isSpecialEventCallSign, isFSeries } from '../../common';
import { Button, DataGrid, DataGridColumn, DataGridExpandOptions, Header, Modal, Label, TextArea } from 'acma-spa';
import {
  Acma_amateurradiocallsign_statuscode,
  Acma_amateurradiocallsignprefixes,
  Acma_amateurradiocallsigntypes,
  IOperatorCallsign,
  IOperatorPayload,
  ICallSignReconfirmationRequest,
  OperatorCallsign,
  getApiClient,
  Acma_amateurradioassessor_statuscode,
} from '../../api';
import { CallsignReconfirmationRequestForm } from './CallsignReconfirmationRequestForm';
import { CallsignTransferApplicationForm } from '../../forms/CallsignTransferApplication';

export interface CallsignsProps extends ClientApiProps {
  rows: IOperatorCallsign[];
  onAuthorisation: () => any;
  onChange: (mode: Partial<IOperatorPayload>) => any;
  onSpinner: (text: string) => any;
}

const expandProps: DataGridExpandOptions<IOperatorCallsign> = {
  content: (row) => { },
  onChange: (selectedRows) => { },
  canExpand: (row) => false,
  rows: [],
};

// Callsign object helpers
const isCallSignApproved = (cs: IOperatorCallsign): boolean => cs?.status === Acma_amateurradiocallsign_statuscode.Approved;
const expireReconfirmByStr = (cs: IOperatorCallsign) => {
  return isCallSignApproved(cs) ? cs.expireOrReconfirmBy?.toLocaleDateString() : undefined;
};

const reapplyFromStr = (cs: IOperatorCallsign) => {
  return isCallSignApproved(cs) && isSpecialEventCallSign(cs.prefix, cs.type, cs.location)
    ? cs.reapplyFrom?.toLocaleDateString() : undefined;
};

const shouldShowReconfirmButton = (cs: IOperatorCallsign): boolean => {
  if (!isSpecialEventCallSign(cs.prefix, cs.type, cs.location)) return true;

  console.log(cs.name);
  const expiresOn = cs.expireOrReconfirmBy || new Date();

  const displayAfter = new Date(expiresOn);
  displayAfter.setMonth(expiresOn.getMonth() - 3);

  console.log(displayAfter);
  console.log(new Date());
  console.log(new Date() > displayAfter);
  console.log(`end ${cs.name}`);

  return new Date() > displayAfter;
};

export const Callsigns: React.FC<CallsignsProps> = (props) => {
  const [transferCallsignTarget, setTransferCallsignTarget] = useState<IOperatorCallsign>();
  const closeTransferDlg = () => setTransferCallsignTarget(undefined); // A helper to force dialog closure

  const [surrenderCallsignTarget, setSurrenderCallsignTarget] = useState<IOperatorCallsign>();
  const closeSurrenderDlg = () => setSurrenderCallsignTarget(undefined); // A helper to force dialog closure

  const [reconfirmCallsignTarget, setReconfirmCallsignTarget] = useState<IOperatorCallsign>();
  const closeReconfirmDlg = () => setReconfirmCallsignTarget(undefined); // A helper to force dialog closure

  const surrender = async (callsign: IOperatorCallsign) => {
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

    try {
      props.onSpinner('Updating...');
      await client.operatorSurrenderCallsign(callsign.name);
      const newRows = props.rows.slice();
      const index = newRows.findIndex((r) => r.name == callsign.name);
      if (index >= 0) {
        newRows.splice(index, 1);
        props.onChange({ callsigns: newRows.map((r) => new OperatorCallsign(r)) });
      }
    } finally {
      props.onSpinner('');
    }
  };

  const updateCallSign = (callsign: IOperatorCallsign | undefined, existsPending: boolean | undefined, reconfirmationDate: Date | undefined) => {
    const rows = props.rows.slice();
    const index = rows.findIndex((r) => r.name == callsign?.name);

    if (index >= 0) {
      existsPending && (rows[index].existsPendingReconfirmationRequest = true);
      reconfirmationDate && (rows[index].expireOrReconfirmBy = reconfirmationDate);
      props.onChange({ callsigns: rows.map((r) => new OperatorCallsign(r)) });
    }
  };

  const columns: DataGridColumn<IOperatorCallsign>[] = [
    { label: 'Call sign', value: (x) => x.name },
    { label: 'Expires / reconfirm by', value: (x) => expireReconfirmByStr(x) },
    { label: 'Reapply from', value: (x) => reapplyFromStr(x) },
    {
      label: '',
      value: (cs) => {
        return (cs.status == Acma_amateurradiocallsign_statuscode.Transferring &&
          <div style={{ textAlign: 'center' }}>
            Transferring
          </div>
        ) || (
            isFSeries(cs.type, cs.letters) ||
            <div style={{ textAlign: 'center' }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setTransferCallsignTarget(cs);
                }}>
                Transfer
              </a>
            </div>
          );
      },
      width: '10',
    },
    {
      label: '',
      value: (cs) => {
        return (cs.status == Acma_amateurradiocallsign_statuscode.Transferring &&
          <></>
        ) || (
            isCallSignApproved(cs) &&
            shouldShowReconfirmButton(cs) &&
            (
              (cs.existsPendingReconfirmationRequest &&
                <div style={{ textAlign: 'center' }}>
                  Reassignment request pending
                </div>
              ) ||
              (
                <div style={{ textAlign: 'center' }}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setReconfirmCallsignTarget(cs);
                    }}>
                    {isSpecialEventCallSign(cs.prefix, cs.type, cs.location) ?
                      "Apply for reassignment" :
                      "Reconfirm"}
                  </a>
                </div>
              )
            )
          );
      },
      width: '20',
    },
    {
      label: '',
      value: (cs) => {
        if ((isCallSignApproved(cs) && !isSpecialEventCallSign(cs.prefix, cs.type, cs.location)) == false)
          return null;

        return (cs.status == Acma_amateurradiocallsign_statuscode.Transferring &&
          <></>
        ) || (
            <div style={{ textAlign: 'center' }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSurrenderCallsignTarget(cs);
                }}>
                Surrender
              </a>
            </div>
          );
      },
      width: '10',
    },
  ];

  const navigate = useNavigate();

  return (
    <>
      <Header label="Assigned call signs">
        <Button label="Apply for a call sign" id="apply-for-callsign" onClick={() => navigate('/forms/amateurradio/callsign')} />
        <Button label="Locate missing call sign" id="locate-callsign" className="ml-2" onClick={() => props.onAuthorisation()} />
      </Header>

      <DataGrid<IOperatorCallsign> expand={expandProps} columns={columns} rows={props.rows} noRowsMessage="No call signs found." />

      <Modal
        id="surrenderCallsignDlg"
        title="Confirm Callsign Surrender"
        open={!!surrenderCallsignTarget}
        onClose={closeSurrenderDlg}
        rightButtons={[
          {
            label: 'Confirm',
            onClick: async () => {
              if (surrenderCallsignTarget) {
                await surrender(surrenderCallsignTarget);
              }

              closeSurrenderDlg();
            },
          },
          { label: 'Cancel', mode: 'secondary', onClick: closeSurrenderDlg },
        ]}>
        <p>You have selected that you would like to surrender your call sign.</p>

        <p>
          Please note that if you click confirm, your call sign will be immediately surrendered and no longer be assigned to you. It will be placed back into
          the public list of available calls signs and others will be able to apply to be assigned your call sign.
        </p>

        <p className="font-weight-bold mb-0">Are you sure you want to proceed and surrender your call sign?</p>
      </Modal>
      <CallsignTransferApplicationForm
        {...props}
        callsignTarget={transferCallsignTarget}
        onSubmit={(callsign) => {
          const rows = props.rows.slice();
          const index = rows.findIndex((r) => r.name == callsign?.name);

          if (index >= 0) {
            rows[index].status = Acma_amateurradiocallsign_statuscode.Transferring;
            props.onChange({ callsigns: rows.map((r) => new OperatorCallsign(r)) });
          }
        }}
        closeDlg={closeTransferDlg}
      >
      </CallsignTransferApplicationForm>
      <CallsignReconfirmationRequestForm
        {...props}
        onAuthorisation={props.onAuthorisation}
        onChange={props.onChange}
        reconfirmCallsignTarget={reconfirmCallsignTarget}
        updateCallsignDetails={(existsPending, reconfirmationDate) => {
          updateCallSign(reconfirmCallsignTarget, existsPending, reconfirmationDate);
        }}
        closeReconfirmDlg={closeReconfirmDlg}></CallsignReconfirmationRequestForm>
    </>
  );
};
